import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'tsh', 'freeT4', 'freeT3',
    'tshDisplay', 'tshStatus', 'tshInterpretation',
    'freeT4Display', 'freeT4Status', 'freeT4Interpretation',
    'freeT3Display', 'freeT3Status', 'freeT3Interpretation',
    'conversionRatio', 'conversionStatus', 'keyInsight'
  ]

  // Ranges based on functional medicine guidelines
  // TSH optimal: 1.0-2.0 mIU/L (standard: 0.4-4.0)
  // Free T4 optimal: 1.1-1.5 ng/dL (standard: 0.8-1.8)
  // Free T3 optimal: 3.0-4.0 pg/mL (standard: 2.3-4.2)

  connect() {
    this.calculate()
  }

  calculate() {
    const tsh = parseFloat(this.tshTarget.value) || 0
    const freeT4 = parseFloat(this.freeT4Target.value) || 0
    const freeT3 = parseFloat(this.freeT3Target.value) || 0

    // TSH interpretation
    if (tsh > 0) {
      this.tshDisplayTarget.textContent = tsh.toFixed(2)
      const tshResult = this.interpretTSH(tsh)
      this.tshStatusTarget.textContent = tshResult.status
      this.tshStatusTarget.className = `px-3 py-1 text-sm font-medium ${tshResult.bgClass}`
      this.tshInterpretationTarget.textContent = tshResult.interpretation
    }

    // Free T4 interpretation
    if (freeT4 > 0) {
      this.freeT4DisplayTarget.textContent = freeT4.toFixed(2)
      const freeT4Result = this.interpretFreeT4(freeT4)
      this.freeT4StatusTarget.textContent = freeT4Result.status
      this.freeT4StatusTarget.className = `px-3 py-1 text-sm font-medium ${freeT4Result.bgClass}`
      this.freeT4InterpretationTarget.textContent = freeT4Result.interpretation
    }

    // Free T3 interpretation
    if (freeT3 > 0) {
      this.freeT3DisplayTarget.textContent = freeT3.toFixed(1)
      const freeT3Result = this.interpretFreeT3(freeT3)
      this.freeT3StatusTarget.textContent = freeT3Result.status
      this.freeT3StatusTarget.className = `px-3 py-1 text-sm font-medium ${freeT3Result.bgClass}`
      this.freeT3InterpretationTarget.textContent = freeT3Result.interpretation
    }

    // Calculate T3:T4 conversion ratio
    if (freeT3 > 0 && freeT4 > 0) {
      // Convert T3 from pg/mL to ng/dL for ratio calculation (divide by 100)
      // T3:T4 ratio typically around 0.27-0.33 for healthy conversion
      const t3InNgDL = freeT3 / 100
      const ratio = (t3InNgDL / freeT4).toFixed(3)
      this.conversionRatioTarget.textContent = ratio

      const conversionResult = this.interpretConversion(parseFloat(ratio))
      this.conversionStatusTarget.textContent = conversionResult.status
      this.conversionStatusTarget.className = `text-lg font-bold ${conversionResult.textClass}`
    } else {
      this.conversionRatioTarget.textContent = '--'
      this.conversionStatusTarget.textContent = '--'
    }

    // Update key insight
    this.updateKeyInsight(tsh, freeT4, freeT3)
  }

  interpretTSH(value) {
    // Standard range: 0.4-4.0 mIU/L
    // Optimal range: 1.0-2.0 mIU/L
    if (value < 0.4) {
      return {
        status: 'LOW',
        bgClass: 'bg-red-100 text-red-800',
        interpretation: 'TSH is below standard range. This may indicate hyperthyroidism (overactive thyroid), ' +
          'excessive thyroid medication, or pituitary issues. Consult your healthcare provider.'
      }
    }
    if (value < 1.0) {
      return {
        status: 'LOW-NORMAL',
        bgClass: 'bg-yellow-100 text-yellow-800',
        interpretation: 'TSH is in the lower end of normal. This can be fine for some, but monitor for ' +
          'hyperthyroid symptoms like anxiety, rapid heartbeat, or unexplained weight loss.'
      }
    }
    if (value <= 2.0) {
      return {
        status: 'OPTIMAL',
        bgClass: 'bg-green-100 text-green-800',
        interpretation: 'Excellent. TSH is in the functional optimal range. This level is associated with ' +
          'good energy, metabolism, and overall thyroid function.'
      }
    }
    if (value <= 4.0) {
      return {
        status: 'HIGH-NORMAL',
        bgClass: 'bg-yellow-100 text-yellow-800',
        interpretation: 'TSH is in the upper normal range. While technically "normal," some people experience ' +
          'subclinical hypothyroid symptoms at this level. Monitor for fatigue, weight gain, or cold intolerance.'
      }
    }
    return {
      status: 'HIGH',
      bgClass: 'bg-red-100 text-red-800',
      interpretation: 'TSH is above standard range, indicating the pituitary is working harder to stimulate ' +
        'an underactive thyroid (hypothyroidism). Consult your healthcare provider.'
    }
  }

  interpretFreeT4(value) {
    // Standard range: 0.8-1.8 ng/dL
    // Optimal range: 1.1-1.5 ng/dL
    if (value < 0.8) {
      return {
        status: 'LOW',
        bgClass: 'bg-red-100 text-red-800',
        interpretation: 'Free T4 is below standard range. This indicates low thyroid hormone production ' +
          'and possible hypothyroidism. Consult your healthcare provider.'
      }
    }
    if (value < 1.1) {
      return {
        status: 'LOW-NORMAL',
        bgClass: 'bg-yellow-100 text-yellow-800',
        interpretation: 'Free T4 is in the lower portion of normal. There may be less substrate available ' +
          'for conversion to active T3. Monitor for hypothyroid symptoms.'
      }
    }
    if (value <= 1.5) {
      return {
        status: 'OPTIMAL',
        bgClass: 'bg-green-100 text-green-800',
        interpretation: 'Excellent. Free T4 is in the optimal range, providing adequate substrate for ' +
          'T3 conversion and supporting healthy metabolism.'
      }
    }
    if (value <= 1.8) {
      return {
        status: 'HIGH-NORMAL',
        bgClass: 'bg-green-50 text-green-700',
        interpretation: 'Free T4 is in the upper normal range. This is generally fine and ensures adequate T3 production capacity.'
      }
    }
    return {
      status: 'HIGH',
      bgClass: 'bg-red-100 text-red-800',
      interpretation: 'Free T4 is above standard range. This may indicate hyperthyroidism or excessive ' +
        'thyroid medication. Consult your healthcare provider.'
    }
  }

  interpretFreeT3(value) {
    // Standard range: 2.3-4.2 pg/mL
    // Optimal range: 3.0-4.0 pg/mL
    if (value < 2.3) {
      return {
        status: 'LOW',
        bgClass: 'bg-red-100 text-red-800',
        interpretation: 'Free T3 is below standard range. Low active thyroid hormone can cause fatigue, ' +
          'weight gain, brain fog, and depression. Consult your healthcare provider.'
      }
    }
    if (value < 3.0) {
      return {
        status: 'LOW-NORMAL',
        bgClass: 'bg-yellow-100 text-yellow-800',
        interpretation: 'Free T3 is in the lower portion of normal. You may experience suboptimal energy ' +
          'and metabolism. Consider factors affecting T4 to T3 conversion like stress, selenium, and inflammation.'
      }
    }
    if (value <= 4.0) {
      return {
        status: 'OPTIMAL',
        bgClass: 'bg-green-100 text-green-800',
        interpretation: 'Excellent. Free T3 is in the optimal range. This level supports good energy, ' +
          'metabolism, cognitive function, and overall wellbeing.'
      }
    }
    if (value <= 4.2) {
      return {
        status: 'HIGH-NORMAL',
        bgClass: 'bg-green-50 text-green-700',
        interpretation: 'Free T3 is at the upper end of normal. This is generally associated with good ' +
          'energy and metabolism, but monitor for hyperthyroid symptoms.'
      }
    }
    return {
      status: 'HIGH',
      bgClass: 'bg-red-100 text-red-800',
      interpretation: 'Free T3 is above standard range. This may indicate hyperthyroidism or T3 ' +
        'supplementation. Watch for anxiety, rapid heart rate, or tremor. Consult your healthcare provider.'
    }
  }

  interpretConversion(ratio) {
    // Optimal T3:T4 ratio is approximately 0.027-0.033 (or 0.27-0.33 depending on unit conversion)
    // Using the ratio calculation: (T3 pg/mL / 100) / T4 ng/dL
    if (ratio < 0.020) {
      return {
        status: 'POOR',
        textClass: 'text-red-600'
      }
    }
    if (ratio < 0.027) {
      return {
        status: 'SUBOPTIMAL',
        textClass: 'text-yellow-600'
      }
    }
    if (ratio <= 0.035) {
      return {
        status: 'OPTIMAL',
        textClass: 'text-green-600'
      }
    }
    return {
      status: 'HIGH',
      textClass: 'text-yellow-600'
    }
  }

  updateKeyInsight(tsh, freeT4, freeT3) {
    let insight = 'TSH alone doesn\'t tell the whole story. Free T3 is the active hormone that affects ' +
      'how you feel, while TSH reflects the brain\'s perception of thyroid function.'

    const hasValues = tsh > 0 || freeT4 > 0 || freeT3 > 0

    if (hasValues) {
      // Check for conversion issue: normal T4 but low T3
      if (freeT4 >= 1.1 && freeT3 > 0 && freeT3 < 3.0) {
        insight = 'Your Free T4 appears adequate but Free T3 is suboptimal. This pattern may suggest a ' +
          'T4 to T3 conversion issue. Factors like stress, inflammation, selenium deficiency, or gut ' +
          'issues can impair conversion.'
      } else if (tsh > 0 && tsh < 2.0 && ((freeT4 > 0 && freeT4 < 1.0) || (freeT3 > 0 && freeT3 < 2.5))) {
        // Check for central hypothyroidism: normal/low TSH with low hormones
        insight = 'TSH is not elevated despite low thyroid hormones. This uncommon pattern may suggest ' +
          'central hypothyroidism (pituitary issue) rather than primary thyroid disease. ' +
          'Discuss with your healthcare provider.'
      } else if (tsh > 4.0 && freeT4 >= 0.8 && freeT4 <= 1.8) {
        // High TSH with normal hormones: subclinical hypothyroidism
        insight = 'Elevated TSH with normal Free T4 suggests subclinical hypothyroidism. Your thyroid ' +
          'is working harder than normal to maintain hormone levels. Monitor for symptom development.'
      } else if (tsh > 0 && tsh < 0.4 && freeT4 >= 0.8 && freeT4 <= 1.8) {
        // Low TSH with normal hormones: subclinical hyperthyroidism
        insight = 'Low TSH with normal Free T4 suggests subclinical hyperthyroidism. This can be due to ' +
          'thyroid nodules, Graves\' disease, or medication. Monitoring is important.'
      } else if (tsh >= 1.0 && tsh <= 2.0 && freeT4 >= 1.1 && freeT4 <= 1.5 && freeT3 >= 3.0 && freeT3 <= 4.0) {
        // All optimal
        insight = 'Your complete thyroid panel looks excellent! TSH, Free T4, and Free T3 are all in ' +
          'the functional optimal ranges, supporting healthy energy and metabolism.'
      }
    }

    this.keyInsightTarget.textContent = insight
  }
}
